/**
 * Кандидаты в логотипы из Instagram (28.09.2026). Картинки профилей собирает
 * `backend/scripts/logo_candidates.py`; в приложение ничего не попадает, пока
 * здесь не нажали «Оставить». Аватарка часто не логотип (фото товара, сезонная
 * версия), поэтому решение за человеком.
 *
 * Один Instagram бывает записан у нескольких заведений (у ТЦ пять арендаторов):
 * тогда галочками отмечаются бренды, которым ставить логотип. Если бренд один,
 * он отмечен сразу.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, Checkbox, Empty, Segmented, Space, Spin, Tag, Typography, message } from 'antd';
import { directoryApi, LogoCandidate } from '../../api/directory';

const { Text } = Typography;
type Filter = 'new' | 'accepted' | 'rejected';

const CandidateCard: React.FC<{ c: LogoCandidate; onDone: () => void }> = ({ c, onDone }) => {
  const [picked, setPicked] = useState<string[]>(c.brands.length === 1 ? [c.brands[0].brand] : []);
  const [busy, setBusy] = useState<'accept' | 'reject' | null>(null);

  const decide = async (d: 'accept' | 'reject') => {
    if (d === 'accept' && picked.length === 0) {
      message.warning('Отметьте, кому ставить логотип');
      return;
    }
    setBusy(d);
    try {
      const r = await directoryApi.decideCandidate(c.handle, d, d === 'accept' ? picked : undefined);
      message.success(d === 'accept' ? `Логотип поставлен на ${r.data.updated} точек` : 'Скрыто');
      onDone();
    } catch (e: unknown) {
      message.error((e as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Не получилось');
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card size="small" style={{ width: 230 }} styles={{ body: { padding: 12 } }}>
      <img src={c.url} alt="" style={{ width: 96, height: 96, borderRadius: 22, border: '1px solid #E3E8F0', display: 'block', margin: '0 auto 10px' }} />
      <a href={c.instagram} target="_blank" rel="noreferrer" style={{ display: 'block', textAlign: 'center', marginBottom: 8 }}>{c.label || `@${c.handle}`}</a>
      <Checkbox.Group
        value={picked}
        onChange={(v) => setPicked(v as string[])}
        style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 10 }}
        disabled={c.status !== 'new'}
      >
        {c.brands.map((b) => (
          <Checkbox key={b.brand} value={b.brand}>
            {b.title} <Text type="secondary">· {b.total}</Text>
          </Checkbox>
        ))}
      </Checkbox.Group>
      {c.status === 'new' ? (
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Button type="primary" loading={busy === 'accept'} disabled={!!busy} onClick={() => decide('accept')}>Оставить</Button>
          <Button loading={busy === 'reject'} disabled={!!busy} onClick={() => decide('reject')}>Не то</Button>
        </Space>
      ) : (
        <Tag color={c.status === 'accepted' ? 'green' : 'default'}>{c.status === 'accepted' ? 'оставлен' : 'скрыт'}</Tag>
      )}
    </Card>
  );
};

const LogoCandidates: React.FC = () => {
  const [filter, setFilter] = useState<Filter>('new');
  const [items, setItems] = useState<LogoCandidate[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await directoryApi.candidates(filter);
      setItems(r.data.items);
      setCounts(r.data.counts);
    } catch {
      message.error('Не удалось загрузить кандидатов');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { void load(); }, [load]);

  return (
    <div>
      <Space style={{ marginBottom: 16 }} wrap>
        <Segmented
          value={filter}
          onChange={(v) => setFilter(v as Filter)}
          options={[
            { value: 'new', label: `Ждут решения · ${counts.new ?? 0}` },
            { value: 'accepted', label: `Оставлены · ${counts.accepted ?? 0}` },
            { value: 'rejected', label: `Скрыты · ${counts.rejected ?? 0}` },
          ]}
        />
        <Button onClick={() => void load()}>Обновить</Button>
        <Text type="secondary">Сбор идёт фоном, новые кандидаты появляются по мере загрузки.</Text>
      </Space>
      {loading ? <Spin /> : items.length === 0 ? <Empty description="Пусто" /> : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
          {items.map((c) => <CandidateCard key={c.handle} c={c} onDone={() => void load()} />)}
        </div>
      )}
    </div>
  );
};

export default LogoCandidates;
